import { safeLocalStorageSetItem } from 'utils/safeLocalStorage';

export const FAVOURITES_CHANGED_EVENT = 'favourites:changed';

export interface FavouriteLayerNode {
  key: string;
  title: string;
  endPoint?: string;
  endpoint?: string;
  type?: string;
  isLeaf?: boolean;
  category?: string;
  categoryName?: string;
  surveySlug?: string;
  selectable?: boolean;
  checkable?: boolean;
  layerSource?: 'survey-wfs';
  children?: FavouriteLayerNode[];
}

export interface FavouritesChangedDetail {
  readonly entries: ReadonlyArray<readonly [string, FavouriteLayerNode]>;
  readonly isAdding: boolean;
}

interface UserDataLike {
  readonly username?: string;
}

function readUsername(): string | undefined {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return undefined;
    }
    const username = (parsed as UserDataLike).username;
    return typeof username === 'string' ? username : undefined;
  } catch {
    return undefined;
  }
}

export function getFavouritesStorageKey(
  tenantId: string | null = localStorage.getItem('tenantId'),
  username: string | undefined = readUsername(),
): string | null {
  if (!tenantId || !username) {
    return null;
  }
  return `favourites_${tenantId}_${username}`;
}

export function loadFavouritesMap(): Map<string, FavouriteLayerNode> {
  const storageKey = getFavouritesStorageKey();
  if (!storageKey) {
    return new Map();
  }
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return new Map();
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return new Map();
    }
    return new Map(parsed as Array<[string, FavouriteLayerNode]>);
  } catch {
    return new Map();
  }
}

export function saveFavouritesMap(
  favourites: Map<string, FavouriteLayerNode>,
): boolean {
  const storageKey = getFavouritesStorageKey();
  if (!storageKey) {
    return false;
  }
  return safeLocalStorageSetItem(
    storageKey,
    JSON.stringify([...favourites.entries()]),
  );
}

export function normalizeFavouriteBaseKey(layerKey: string): string {
  return layerKey.replace(/^favourites_/, '');
}

export function isFavouriteKey(
  favourites: ReadonlyMap<string, FavouriteLayerNode>,
  layerKey: string,
): boolean {
  const rawKey = normalizeFavouriteBaseKey(layerKey);
  return (
    favourites.has(rawKey) ||
    favourites.has(`favourites_${rawKey}`) ||
    favourites.has(layerKey)
  );
}

export function getFavouriteBaseKeys(
  favourites: ReadonlyMap<string, FavouriteLayerNode>,
): Set<string> {
  const keys = new Set<string>();
  for (const key of favourites.keys()) {
    keys.add(normalizeFavouriteBaseKey(key));
  }
  return keys;
}

export function getFavouriteDisplayNodes(
  favourites: ReadonlyMap<string, FavouriteLayerNode>,
): FavouriteLayerNode[] {
  const seen = new Set<string>();
  const nodes: FavouriteLayerNode[] = [];

  for (const [mapKey, node] of favourites.entries()) {
    if (!mapKey.startsWith('favourites_')) {
      continue;
    }
    const rawKey = normalizeFavouriteBaseKey(node.key);
    if (seen.has(rawKey)) {
      continue;
    }
    seen.add(rawKey);
    nodes.push({ ...node, key: rawKey });
  }

  if (nodes.length > 0) {
    return nodes;
  }

  for (const [mapKey, node] of favourites.entries()) {
    if (mapKey.startsWith('favourites_')) {
      continue;
    }
    const rawKey = normalizeFavouriteBaseKey(mapKey);
    if (seen.has(rawKey)) {
      continue;
    }
    seen.add(rawKey);
    nodes.push({ ...node, key: rawKey });
  }

  return nodes;
}

export function toggleFavourite(
  favourites: ReadonlyMap<string, FavouriteLayerNode>,
  nodeData: FavouriteLayerNode,
  parentName: string,
): { readonly favourites: Map<string, FavouriteLayerNode>; readonly isAdding: boolean } {
  const next = new Map(favourites);
  const rawKey = normalizeFavouriteBaseKey(nodeData.key);

  if (
    next.has(rawKey) ||
    next.has(`favourites_${rawKey}`) ||
    next.has(nodeData.key)
  ) {
    next.delete(rawKey);
    next.delete(`favourites_${rawKey}`);
    next.delete(nodeData.key);
    return { favourites: next, isAdding: false };
  }

  const baseNode: FavouriteLayerNode = {
    ...nodeData,
    key: rawKey,
    isLeaf: true,
  };
  next.set(rawKey, baseNode);
  next.set(`favourites_${rawKey}`, {
    ...baseNode,
    title: `${nodeData.title} (${parentName})`,
    categoryName: parentName,
  });
  return { favourites: next, isAdding: true };
}

export function dispatchFavouritesChanged(
  favourites: ReadonlyMap<string, FavouriteLayerNode>,
  isAdding: boolean,
): void {
  const detail: FavouritesChangedDetail = {
    entries: [...favourites.entries()],
    isAdding,
  };
  window.dispatchEvent(
    new CustomEvent<FavouritesChangedDetail>(FAVOURITES_CHANGED_EVENT, {
      detail,
    }),
  );
}

export function buildSurveyLayerFavouriteNode(
  layer: {
    readonly key: string;
    readonly name: string;
    readonly type: string;
    readonly endpoint: string;
  },
  surveySlug: string,
): FavouriteLayerNode {
  return {
    key: layer.key,
    title: layer.name,
    endPoint: layer.endpoint,
    endpoint: layer.endpoint,
    type: layer.type,
    isLeaf: true,
    surveySlug,
  };
}
